/**
 * Implementation group-attribute bindings.
 *
 * A third data-binding kind (alongside `gi_field` and `service_option`) that
 * points an implementation task at an attribute living on the group / account /
 * opportunity / team — values that are NOT authored inside the implementation
 * but referenced/confirmed from their canonical home.
 *
 *   dataBindingEntity = 'group_attribute'
 *   dataBindingField  = one of GroupAttributeKey below
 *
 * `editable: true` means the value can be written from the implementation
 * checklist (currently only Legal Name, which writes back to the OpsGroup
 * snapshot). The rest are read-only references resolved from their source.
 */

export const GROUP_ATTRIBUTE_ENTITY = 'group_attribute';

export type GroupAttributeKey =
  | 'legal_name'
  | 'other_names'
  | 'broker_of_record'
  | 'sales_rep'
  | 'account_exec'
  | 'implementation_manager';

export interface GroupAttributeMeta {
  key: GroupAttributeKey;
  label: string;
  /** Can the value be edited from the implementation checklist? */
  editable: boolean;
  /** Human description of where the value comes from. */
  source: string;
}

export const GROUP_ATTRIBUTES: GroupAttributeMeta[] = [
  { key: 'legal_name', label: 'Legal Name', editable: true, source: 'Group record' },
  { key: 'other_names', label: 'Other Names', editable: true, source: 'Group record' },
  { key: 'broker_of_record', label: 'Broker of Record', editable: false, source: 'CRM Account' },
  { key: 'sales_rep', label: 'Sales Rep', editable: false, source: 'Winning opportunity owner' },
  { key: 'account_exec', label: 'Account Exec', editable: false, source: 'CRM Account owner' },
  { key: 'implementation_manager', label: 'Implementation Manager', editable: false, source: 'Team card' },
];

const BY_KEY = new Map<string, GroupAttributeMeta>(GROUP_ATTRIBUTES.map((a) => [a.key, a]));

export function groupAttributeMeta(key?: string | null): GroupAttributeMeta | undefined {
  return key ? BY_KEY.get(key) : undefined;
}
